import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  CommandLineIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

/* ─── Command Groups ─── */

const commandGroups = [
  {
    category: 'Getting Started',
    commands: [
      {
        title: 'Installation',
        id: 'install',
        content: `npm install -g uiquarter`,
        description: 'Requires Node.js >= 18. Works on macOS, Linux, and Windows.',
      },
    ],
  },
  {
    category: 'Analysis',
    commands: [
      {
        title: 'uiquarter init',
        id: 'init',
        content: `uiquarter init -d ./my-project
uiquarter init --no-cache
uiquarter init --debug`,
        description: 'Run the full analysis pipeline: file discovery, 20 analyzers, normalization, indexing, and insight generation. Produces a .uiq/ directory with the intelligence index.',
      },
      {
        title: 'uiquarter watch',
        id: 'watch',
        content: `uiquarter watch -d ./my-project
uiquarter watch --debounce 500 --verbose`,
        description: 'Watch for file changes and re-analyze incrementally.',
      },
      {
        title: 'uiquarter explain',
        id: 'explain',
        content: `uiquarter explain -d ./my-project`,
        description: 'Print a human-readable project architecture summary.',
      },
    ],
  },
  {
    category: 'Querying',
    commands: [
      {
        title: 'uiquarter query',
        id: 'query',
        content: `uiquarter query stats
uiquarter query component Button
uiquarter query deps Dashboard
uiquarter query dependents Button
uiquarter query hubs
uiquarter query chains
uiquarter query insights hub-component`,
        description: 'Query the intelligence index. Subcommands: stats, component, deps, dependents, hubs, chains, insights.',
      },
      {
        title: 'uiquarter resolve',
        id: 'resolve',
        content: `uiquarter resolve "add dark mode toggle"
uiquarter resolve "fix modal accessibility" --synonyms --fuzzy
uiquarter resolve "refactor button" --debug --format md`,
        description: 'Match a free-form task description to relevant patterns using inverted index + synonym + fuzzy matching.',
      },
      {
        title: 'uiquarter prompt',
        id: 'prompt',
        content: `uiquarter prompt --budget 2000 --format md`,
        description: 'Generate AI-optimized architecture context with optional token budget.',
      },
    ],
  },
  {
    category: 'Generation',
    commands: [
      {
        title: 'uiquarter generate',
        id: 'generate',
        content: `uiquarter generate --target all
uiquarter generate --target claude
uiquarter generate --target cursor
uiquarter generate --dry-run`,
        description: 'Generate AI tool context files. Targets: claude (CLAUDE.md), codex (AGENTS.md), cursor (.cursorrules), windsurf (.windsurfrules), cline (.clinerules), copilot (copilot-instructions.md), aider (CONVENTIONS.md).',
      },
      {
        title: 'uiquarter report',
        id: 'report',
        content: `uiquarter report
uiquarter report --output dashboard.html
uiquarter report --open`,
        description: 'Generate a self-contained HTML dashboard with dependency graphs (Mermaid), component tables, and insights.',
      },
    ],
  },
  {
    category: 'DevOps',
    commands: [
      {
        title: 'uiquarter ci',
        id: 'ci',
        content: `uiquarter ci
uiquarter ci --fail-on warning
uiquarter ci --format md --output report.md
uiquarter ci --pr 42 --repo owner/repo
uiquarter ci --template`,
        description: 'CI/CD analysis -- detect regressions, optionally post GitHub PR comments. Use --template to generate a GitHub Action workflow.',
      },
      {
        title: 'uiquarter drift',
        id: 'drift',
        content: `uiquarter drift --save
uiquarter drift
uiquarter drift --format json
uiquarter drift --compare main`,
        description: 'Detect changes between a saved baseline snapshot and the current analysis. Use --compare to diff against another git branch.',
      },
      {
        title: 'uiquarter lint',
        id: 'lint',
        content: `uiquarter lint
uiquarter lint --format md
uiquarter lint --format json`,
        description: 'Check project conventions against configurable rules. Configure in .uiqrc.json.',
      },
      {
        title: 'uiquarter serve',
        id: 'serve',
        content: `uiquarter serve
uiquarter serve --transport http --port 3100`,
        description: 'Start an MCP (Model Context Protocol) server for real-time AI tool integration. Supports stdio and HTTP transports.',
      },
    ],
  },
];

/* Extra sections */
const configExample = `{
  "rules": {
    "file-naming": { "enabled": true },
    "dir-naming": { "enabled": true },
    "barrel-exports": { "enabled": true },
    "circular-deps": { "enabled": true },
    "single-styling": { "enabled": true },
    "min-accessibility": { "enabled": true, "threshold": 0.5 },
    "max-nesting-depth": { "enabled": true, "limit": 8 }
  }
}`;

const outputStructure = `.uiq/
  index.json          # master intelligence index
  meta.json           # hash, timestamps, stats, build number
  insights.json       # architectural insights
  snapshot.json       # drift detection baseline
  cache/              # analyzer result cache
  patterns/           # per-pattern detail files`;

/* Flat list of all section IDs for observer */
const allSectionIds = [
  ...commandGroups.flatMap((g) => g.commands.map((c) => c.id)),
  'configuration',
  'output-structure',
];

/* ─── Gradient Border Box ─── */
const GradientBox = ({ children, className = '' }) => (
  <div className={`relative rounded-2xl p-px bg-gradient-to-br from-[#FF3366]/40 via-[#FF6B4D]/20 to-[#FF3366]/10 ${className}`}>
    <div className="rounded-2xl bg-[#0A0A10] p-8 md:p-10">
      {children}
    </div>
  </div>
);

/* ─── Terminal Shell ─── */
const TerminalBlock = ({ title, children }) => (
  <div className="rounded-xl border border-white/10 bg-[#0D0D12] overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-[#0A0A0F]">
      <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
      {title && <span className="ml-2 text-xs text-[#B4A5BD] font-mono">{title}</span>}
    </div>
    <div className="p-5 font-mono text-sm leading-relaxed">{children}</div>
  </div>
);

/* ─── Syntax-highlighted code line ─── */
function CodeLine({ line }) {
  if (!line.trim()) return <div className="h-3" />;

  if (line.trim().startsWith('#') || line.trim().startsWith('//')) {
    return <div className="text-[#6A5F73]">{line}</div>;
  }

  const jsonMatch = line.match(/^(\s*)"([^"]+)"(\s*:\s*)(.*)/);
  if (jsonMatch) {
    return (
      <div>
        <span className="text-[#B4A5BD]">{jsonMatch[1]}</span>
        <span className="text-[#FF6B4D]">"{jsonMatch[2]}"</span>
        <span className="text-[#B4A5BD]">{jsonMatch[3]}</span>
        <span className="text-[#B4A5BD]">{jsonMatch[4]}</span>
      </div>
    );
  }

  const cmdMatch = line.match(/^(uiquarter|npm)\s+(.*)/);
  if (cmdMatch) {
    const parts = cmdMatch[2].split(/\s+/);
    return (
      <div>
        <span className="text-[#FF3366]">$ </span>
        <span className="text-white">{cmdMatch[1]}</span>{' '}
        {parts.map((part, i) => {
          if (part.startsWith('--')) {
            return <span key={i}><span className="text-[#FF6B4D]">{part}</span>{i < parts.length - 1 ? ' ' : ''}</span>;
          }
          if (part.startsWith('"') || part.startsWith("'")) {
            return <span key={i}><span className="text-green-400">{part}</span>{i < parts.length - 1 ? ' ' : ''}</span>;
          }
          return <span key={i}><span className="text-[#B4A5BD]">{part}</span>{i < parts.length - 1 ? ' ' : ''}</span>;
        })}
      </div>
    );
  }

  if (line.includes('/') && !line.includes('http')) {
    const commentIdx = line.indexOf('#');
    if (commentIdx > -1) {
      return (
        <div>
          <span className="text-white">{line.substring(0, commentIdx)}</span>
          <span className="text-[#6A5F73]">{line.substring(commentIdx)}</span>
        </div>
      );
    }
    return <div className="text-white">{line}</div>;
  }

  return <div className="text-[#B4A5BD]">{line}</div>;
}

function CodeContent({ code }) {
  return code.split('\n').map((line, i) => <CodeLine key={i} line={line} />);
}

/* ─── Sidebar ─── */
function Sidebar({ activeSection, onNavigate, className = '' }) {
  return (
    <nav className={className}>
      {commandGroups.map((group) => (
        <div key={group.category} className="mb-6">
          <div className="text-xs uppercase tracking-wider text-[#B4A5BD]/50 font-semibold mb-2 px-3">
            {group.category}
          </div>
          {group.commands.map((cmd) => (
            <button
              key={cmd.id}
              onClick={() => onNavigate(cmd.id)}
              className={`
                w-full text-left text-sm px-3 py-1.5 rounded-md transition-all duration-200
                ${activeSection === cmd.id
                  ? 'text-white bg-white/[0.05] border-l-2 border-[#FF3366] pl-[10px]'
                  : 'text-[#B4A5BD] hover:text-white hover:bg-white/[0.03] border-l-2 border-transparent pl-[10px]'
                }
              `}
            >
              {cmd.id === 'install' ? 'Installation' : cmd.id}
            </button>
          ))}
        </div>
      ))}

      {/* Extra sections */}
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-[#B4A5BD]/50 font-semibold mb-2 px-3">
          Reference
        </div>
        {[
          { id: 'configuration', label: 'Configuration' },
          { id: 'output-structure', label: 'Output Structure' },
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => onNavigate(item.id)}
            className={`
              w-full text-left text-sm px-3 py-1.5 rounded-md transition-all duration-200
              ${activeSection === item.id
                ? 'text-white bg-white/[0.05] border-l-2 border-[#FF3366] pl-[10px]'
                : 'text-[#B4A5BD] hover:text-white hover:bg-white/[0.03] border-l-2 border-transparent pl-[10px]'
              }
            `}
          >
            {item.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

/* ─── Mobile Jump-To Dropdown ─── */
function MobileNav({ activeSection, onNavigate }) {
  const [isOpen, setIsOpen] = useState(false);

  const activeLabel = (() => {
    for (const group of commandGroups) {
      const found = group.commands.find((c) => c.id === activeSection);
      if (found) return found.title;
    }
    if (activeSection === 'configuration') return 'Configuration';
    if (activeSection === 'output-structure') return 'Output Structure';
    return 'Installation';
  })();

  return (
    <div className="lg:hidden sticky top-0 z-30 bg-[#0A0B0F]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl border border-white/10 bg-white/[0.03] text-sm"
        >
          <span className="text-[#B4A5BD]">
            Jump to: <span className="text-white font-medium">{activeLabel}</span>
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-[#B4A5BD] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-2 rounded-xl border border-white/10 bg-[#0D0D12] p-3 max-h-80 overflow-y-auto"
          >
            {commandGroups.map((group) => (
              <div key={group.category} className="mb-3">
                <div className="text-xs uppercase tracking-wider text-[#B4A5BD]/50 font-semibold mb-1 px-2">
                  {group.category}
                </div>
                {group.commands.map((cmd) => (
                  <button
                    key={cmd.id}
                    onClick={() => { onNavigate(cmd.id); setIsOpen(false); }}
                    className={`
                      w-full text-left text-sm px-2 py-1.5 rounded-md transition-colors
                      ${activeSection === cmd.id ? 'text-white bg-white/[0.05]' : 'text-[#B4A5BD] hover:text-white'}
                    `}
                  >
                    {cmd.title}
                  </button>
                ))}
              </div>
            ))}
            <div className="mb-1">
              <div className="text-xs uppercase tracking-wider text-[#B4A5BD]/50 font-semibold mb-1 px-2">Reference</div>
              <button onClick={() => { onNavigate('configuration'); setIsOpen(false); }} className={`w-full text-left text-sm px-2 py-1.5 rounded-md ${activeSection === 'configuration' ? 'text-white bg-white/[0.05]' : 'text-[#B4A5BD] hover:text-white'}`}>Configuration</button>
              <button onClick={() => { onNavigate('output-structure'); setIsOpen(false); }} className={`w-full text-left text-sm px-2 py-1.5 rounded-md ${activeSection === 'output-structure' ? 'text-white bg-white/[0.05]' : 'text-[#B4A5BD] hover:text-white'}`}>Output Structure</button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

/* ─── Command Section (lightweight) ─── */
function CommandSection({ command, isLast }) {
  return (
    <div id={command.id} className={`scroll-mt-24 py-10 ${!isLast ? 'border-b border-white/5' : ''}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] flex-shrink-0" />
        <h3 className="text-xl font-bold font-mono text-white">{command.title}</h3>
      </div>
      <p className="text-[#B4A5BD] text-sm md:text-base leading-relaxed mb-5 ml-5">
        {command.description}
      </p>
      <div className="ml-5">
        <TerminalBlock title={command.id}>
          <CodeContent code={command.content} />
        </TerminalBlock>
      </div>
    </div>
  );
}

/* ─── Page ─── */
export default function Docs() {
  const [activeSection, setActiveSection] = useState('install');
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const scrollToSection = useCallback((id) => {
    setActiveSection(id);

    /* Set scrolling flag to prevent observer from overriding */
    isScrollingRef.current = true;
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 800);

    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  /* Track which section is visible */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingRef.current) return;
        const visible = entries.find((e) => e.isIntersecting);
        if (visible) {
          setActiveSection(visible.target.id);
        }
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 }
    );

    allSectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative overflow-hidden">
      <SEO
        title="Documentation - UIQuarter"
        description="Complete documentation for UIQuarter CLI commands, configuration, and output structure."
      />

      {/* Background glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF3366]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-96 right-1/4 w-96 h-96 bg-[#FF6B4D]/5 rounded-full blur-3xl pointer-events-none" />

      {/* ── Hero ── */}
      <section className="relative pt-28 pb-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                Documentation
              </span>
            </h1>
            <p className="text-lg text-[#B4A5BD] max-w-2xl mx-auto">
              13 commands to analyze, query, generate, and integrate.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Mobile Jump-To Nav ── */}
      <MobileNav activeSection={activeSection} onNavigate={scrollToSection} />

      {/* ── Sidebar + Content Layout ── */}
      <section className="max-w-6xl mx-auto px-4 pb-20">
        <div className="flex gap-10">

          {/* Sidebar — desktop only */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <Sidebar
              activeSection={activeSection}
              onNavigate={scrollToSection}
              className="sticky top-24"
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">

            {/* Command Groups */}
            {commandGroups.map((group) => (
              <div key={group.category}>
                {group.commands.map((cmd, i) => {
                  const isLastInGroup = i === group.commands.length - 1;
                  const isLastGroup = group === commandGroups[commandGroups.length - 1];
                  const isLast = isLastInGroup && isLastGroup;
                  return (
                    <CommandSection key={cmd.id} command={cmd} isLast={isLast} />
                  );
                })}
              </div>
            ))}

            {/* ── Configuration ── */}
            <div id="configuration" className="scroll-mt-24 py-10 border-b border-white/5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] flex-shrink-0" />
                <h3 className="text-xl font-bold text-white">Configuration</h3>
              </div>
              <p className="text-[#B4A5BD] text-sm md:text-base leading-relaxed mb-5 ml-5">
                Create a{' '}
                <code className="text-[#FF3366] bg-[#FF3366]/10 px-1.5 py-0.5 rounded text-xs font-mono">
                  .uiqrc.json
                </code>{' '}
                in your project root to configure convention rules. Each rule can be toggled
                independently, and some accept additional thresholds or limits.
              </p>
              <div className="ml-5">
                <GradientBox>
                  <TerminalBlock title=".uiqrc.json">
                    <CodeContent code={configExample} />
                  </TerminalBlock>
                </GradientBox>
              </div>
            </div>

            {/* ── Output Structure ── */}
            <div id="output-structure" className="scroll-mt-24 py-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] flex-shrink-0" />
                <h3 className="text-xl font-bold text-white">Output Structure</h3>
              </div>
              <p className="text-[#B4A5BD] text-sm md:text-base leading-relaxed mb-5 ml-5">
                After running{' '}
                <code className="text-[#FF3366] bg-[#FF3366]/10 px-1.5 py-0.5 rounded text-xs font-mono">
                  uiquarter init
                </code>
                , a{' '}
                <code className="text-[#FF3366] bg-[#FF3366]/10 px-1.5 py-0.5 rounded text-xs font-mono">
                  .uiq/
                </code>{' '}
                directory is created with the full intelligence index.
              </p>
              <div className="ml-5">
                <GradientBox>
                  <TerminalBlock title="project structure">
                    <CodeContent code={outputStructure} />
                  </TerminalBlock>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { file: 'index.json', desc: 'Master intelligence index with all patterns and edges' },
                      { file: 'meta.json', desc: 'Build metadata, timestamps, and project statistics' },
                      { file: 'insights.json', desc: 'Detected architectural smells and recommendations' },
                      { file: 'snapshot.json', desc: 'Baseline snapshot for drift detection comparisons' },
                      { file: 'cache/', desc: 'Cached analyzer results for incremental re-analysis' },
                      { file: 'patterns/', desc: 'Individual pattern detail files for deep queries' },
                    ].map((item) => (
                      <div
                        key={item.file}
                        className="flex items-start gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02]"
                      >
                        <code className="text-[#FF3366] text-xs font-mono bg-[#FF3366]/10 px-2 py-1 rounded flex-shrink-0">
                          {item.file}
                        </code>
                        <span className="text-[#B4A5BD] text-xs leading-relaxed">{item.desc}</span>
                      </div>
                    ))}
                  </div>
                </GradientBox>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
